/**
 * Pictoirel Design Studio — Print Quality Indicators (Phase 15)
 *
 * Implements:
 * 1. Safe Zone Visual Guide (15A)
 * 2. Low Resolution Warning Badges (15B)
 *
 * Attaches automatically to window.fabricCanvas.
 */
(function() {
  'use strict';

  // ─── 1. Injected CSS ──────────────────────────────────────────
  const style = document.createElement('style');
  style.textContent = `
    .ds-safe-zone-guide {
      position: absolute;
      top: 130px; left: 130px; /* 100px bleed + 30px safe margin */
      width: 740px; height: 506px; /* 1000-260, 766-260 */
      border: 1px dashed rgba(200, 100, 100, 0.4);
      pointer-events: none;
      z-index: 48;
      display: none;
    }
    .ds-safe-zone-guide.is-visible {
      display: block;
    }
    .ds-quality-badge {
      position: absolute;
      background: #dc2626;
      color: #fff;
      font-size: 11px;
      font-family: 'Inter', sans-serif;
      font-weight: 600;
      padding: 3px 6px;
      border-radius: 4px;
      pointer-events: none;
      z-index: 50;
      transform: translate(-50%, -100%);
      white-space: nowrap;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    }
    .ds-quality-badge::after {
      content: '';
      position: absolute;
      bottom: -4px;
      left: 50%;
      margin-left: -4px;
      border-width: 4px 4px 0;
      border-style: solid;
      border-color: #dc2626 transparent transparent transparent;
    }
  `;
  document.head.appendChild(style);

  // ─── 2. Safe Zone Guide ───────────────────────────────────────
  
  let safeZoneEl = null;

  function initSafeZone() {
    const container = document.getElementById('ds-canvas-container');
    if (!container) return;
    
    if (!document.getElementById('ds-safe-zone')) {
      safeZoneEl = document.createElement('div');
      safeZoneEl.id = 'ds-safe-zone';
      safeZoneEl.className = 'ds-safe-zone-guide';
      // Insert before the fabric canvas wrapper if possible
      container.appendChild(safeZoneEl);
    }
  }

  window.toggleSafeZoneGuide = function(forceState) {
    if (!safeZoneEl) initSafeZone();
    if (!safeZoneEl) return;
    
    if (typeof forceState === 'boolean') {
      safeZoneEl.classList.toggle('is-visible', forceState);
    } else {
      safeZoneEl.classList.toggle('is-visible');
    }
  };

  // ─── 3. Resolution Badges ─────────────────────────────────────
  
  const activeBadges = new Map(); // Map object reference -> badge DOM element

  function checkImageResolution(obj) {
    if (!obj || obj.type !== 'image' || obj.isBackground || obj.isSticker) return;
    
    // For print quality (300dpi), we need the source image to be roughly 2.4x the canvas display size 
    // since canvas is 125dpi (1000px = 8 inches).
    const printW = obj.width * obj.scaleX;
    const printH = obj.height * obj.scaleY;
    
    const minSourceW = printW * 2.4;
    const minSourceH = printH * 2.4;

    const sourceW = obj.originalWidth || obj.width;
    const sourceH = obj.originalHeight || obj.height;

    const isLowRes = sourceW < minSourceW || sourceH < minSourceH;

    if (isLowRes) {
      showBadge(obj, '⚠ Low Res');
    } else {
      hideBadge(obj);
    }
  }

  function showBadge(obj, text) {
    const container = document.getElementById('ds-canvas-container');
    if (!container) return;

    let badge = activeBadges.get(obj);
    if (!badge) {
      badge = document.createElement('div');
      badge.className = 'ds-quality-badge';
      container.appendChild(badge);
      activeBadges.set(obj, badge);
    }

    badge.textContent = text;
    updateBadgePosition(obj);
  }

  function hideBadge(obj) {
    const badge = activeBadges.get(obj);
    if (badge) {
      if (badge.parentNode) badge.parentNode.removeChild(badge);
      activeBadges.delete(obj);
    }
  }

  function updateBadgePosition(obj) {
    const badge = activeBadges.get(obj);
    if (!badge || !window.fabricCanvas) return;

    // MINOR-02 FIX: Use getBoundingRect to handle rotation and canvas zoom correctly
    const bound = obj.getBoundingRect(true, true);
    
    // Position badge centered above the object
    var badgeX = bound.left + (bound.width / 2);
    var badgeY = bound.top - 8; // 8px above the top edge

    // Clamp so badge stays visible within canvas area
    const cw = window.fabricCanvas.getWidth() || 1000;
    const ch = window.fabricCanvas.getHeight() || 766;
    
    badgeX = Math.max(50, Math.min(cw - 50, badgeX));
    badgeY = Math.max(4, Math.min(ch - 20, badgeY));

    badge.style.left = `${badgeX}px`;
    badge.style.top = `${badgeY}px`;
  }

  function checkAllResolutions() {
    if (!window.fabricCanvas) return;
    window.fabricCanvas.getObjects().forEach(checkImageResolution);
  }

  function clearAllBadges() {
    activeBadges.forEach(badge => {
      if (badge.parentNode) badge.parentNode.removeChild(badge);
    });
    activeBadges.clear();
  }

  // ─── 4. Canvas Event Hooks ────────────────────────────────────
  
  // We attach hooks once the canvas is available
  let isHooked = false;
  
  function hookCanvasEvents() {
    if (isHooked || !window.fabricCanvas) return;
    
    const fc = window.fabricCanvas;

    fc.on('object:modified', function(e) {
      if (e.target) checkImageResolution(e.target);
    });

    fc.on('object:scaling', function(e) {
      if (e.target) {
        checkImageResolution(e.target);
        updateBadgePosition(e.target);
      }
    });

    fc.on('object:moving', function(e) {
      if (e.target) updateBadgePosition(e.target);
    });

    // Clean up when an object is removed
    fc.on('object:removed', function(e) {
      if (e.target) hideBadge(e.target);
    });

    // Recheck all when spread changes (we can listen to custom event or polling, 
    // but the easiest is intercepting loadSpreadFromMemoryData implicitly if possible,
    // or just exposing a recheck function)
    isHooked = true;
  }

  window.initQualityIndicators = function(fc) {
    if (fc) window.fabricCanvas = fc;
    if (!window.fabricCanvas) return;
    hookCanvasEvents();
    initSafeZone();
  };

  // Expose global methods for modal integration
  window.dsQuality = {
    toggleSafeZone: window.toggleSafeZoneGuide,
    recheckResolutions: checkAllResolutions,
    clearBadges: clearAllBadges
  };

})();
